import { useBodyPalette } from "../../scene/BodyFinish";
import { Chassis } from "../../scene/Chassis";
import { GLASS, HEADLIGHT } from "../../scene/materials";
import { beamBetween } from "../geometry/panels";
import { JCB } from "./dimensions";
const LOADER_Z = 0.62;
const STABILISER_Z = 0.6;
const CAB = {
  centreX: -0.3,
  floorY: 1.34,
  roofY: 2.9,
  width: 1.36,
  length: 1.58
};
const pair = [-1, 1];
function JcbBody() {
  const palette = useBodyPalette("jcb");
  const cabGlassHeight = CAB.roofY - CAB.floorY - 0.1;
  const cabGlassCentreY = CAB.floorY + 0.05 + cabGlassHeight / 2;
  const pillarX = CAB.length / 2 - 0.045;
  const pillarZ = CAB.width / 2 - 0.045;
  const loaderArm = beamBetween([0.5, 1.42], [2.3, 0.6], {
    thickness: 0.17,
    depth: 0.15
  });
  const loaderRam = beamBetween([0.72, 1.02], [1.8, 0.78], {
    thickness: 0.09,
    depth: 0.09
  });
  const bucketFloor = beamBetween([2.32, 0.3], [2.86, 0.2], {
    thickness: 0.09,
    depth: 2.1
  });
  const boom = beamBetween([-1.88, 1.72], [-1.32, 2.72], { thickness: 0.24, depth: 0.26 });
  const dipper = beamBetween([-1.32, 2.72], [-2.2, 2.05], { thickness: 0.2, depth: 0.22 });
  const stabiliser = beamBetween([-2.02, 1.12], [-2.34, 0.18], {
    thickness: 0.14,
    depth: 0.16
  });
  return <group>
      <Chassis>
        {
    /* Main frame and axle housings. A backhoe has no bodywork over its wheels. */
  }
        <mesh position={[-0.1, 0.9, 0]}>
          <boxGeometry args={[3.3, 0.56, 1.15]} />
          <meshPhysicalMaterial {...palette.body} />
        </mesh>
        <mesh position={[JCB.frontAxleX, 0.6, 0]}>
          <boxGeometry args={[0.55, 0.26, 1.55]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
        <mesh position={[JCB.rearAxleX, 0.7, 0]}>
          <boxGeometry args={[0.6, 0.32, 1.75]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
      </Chassis>

      {
    /* Engine hood ahead of the cab, with grille and work lamps. */
  }
      <mesh position={[1.13, 1.2, 0]}>
        <boxGeometry args={[1.15, 0.6, 1.06]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[1.75, 1.18, 0]}>
        <boxGeometry args={[0.1, 0.42, 0.9]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {pair.map((side) => <mesh key={`lamp-${side}`} position={[1.76, 1.4, side * 0.34]}>
          <boxGeometry args={[0.08, 0.16, 0.2]} />
          <meshPhysicalMaterial {...HEADLIGHT} />
        </mesh>)}
      <mesh position={[1.58, 1.78, 0.34]}>
        <cylinderGeometry args={[0.05, 0.05, 0.55, 16]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>

      {
    /* Cab: a glazed box inside a yellow ROPS frame. */
  }
      <mesh position={[CAB.centreX, CAB.floorY, 0]}>
        <boxGeometry args={[CAB.length, 0.1, CAB.width]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[CAB.centreX, cabGlassCentreY, 0]}>
        <boxGeometry args={[CAB.length - 0.12, cabGlassHeight, CAB.width - 0.08]} />
        <meshPhysicalMaterial {...GLASS} />
      </mesh>
      <mesh position={[CAB.centreX, CAB.roofY, 0]}>
        <boxGeometry args={[CAB.length + 0.08, 0.12, CAB.width + 0.08]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      {pair.flatMap(
    (sideX) => pair.map((sideZ) => <mesh
      key={`pillar-${sideX}-${sideZ}`}
      position={[CAB.centreX + sideX * pillarX, cabGlassCentreY, sideZ * pillarZ]}
    >
            <boxGeometry args={[0.09, cabGlassHeight + 0.06, 0.09]} />
            <meshPhysicalMaterial {...palette.body} />
          </mesh>)
  )}
      <mesh position={[CAB.centreX, CAB.roofY + 0.13, 0.5]}>
        <cylinderGeometry args={[0.07, 0.07, 0.15, 16]} />
        <meshPhysicalMaterial {...palette.accent} />
      </mesh>

      {
    /* Loader arms, rams and bucket at the front. */
  }
      {pair.map((side) => <group key={`loader-${side}`}>
          <mesh
    position={[loaderArm.position[0], loaderArm.position[1], side * LOADER_Z]}
    rotation={loaderArm.rotation}
  >
            <boxGeometry args={loaderArm.args} />
            <meshPhysicalMaterial {...palette.body} />
          </mesh>
          <mesh
    position={[loaderRam.position[0], loaderRam.position[1], side * LOADER_Z]}
    rotation={loaderRam.rotation}
  >
            <boxGeometry args={loaderRam.args} />
            <meshPhysicalMaterial {...palette.edge} />
          </mesh>
        </group>)}
      <mesh position={[2.32, 0.6, 0]}>
        <boxGeometry args={[0.1, 0.66, 2.1]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={bucketFloor.position} rotation={bucketFloor.rotation}>
        <boxGeometry args={bucketFloor.args} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[2.9, 0.18, 0]}>
        <boxGeometry args={[0.16, 0.05, 2.12]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>
      {pair.map((side) => <mesh key={`bucket-side-${side}`} position={[2.58, 0.42, side * 1.02]}>
          <boxGeometry args={[0.6, 0.5, 0.06]} />
          <meshPhysicalMaterial {...palette.body} />
        </mesh>)}

      {
    /* Backhoe stowed over the rear: kingpost, boom folded up, dipper folded back. */
  }
      <mesh position={[-1.98, 1.3, 0]}>
        <boxGeometry args={[0.32, 0.95, 0.85]} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={boom.position} rotation={boom.rotation}>
        <boxGeometry args={boom.args} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={dipper.position} rotation={dipper.rotation}>
        <boxGeometry args={dipper.args} />
        <meshPhysicalMaterial {...palette.body} />
      </mesh>
      <mesh position={[-2.34, 1.82, 0]}>
        <boxGeometry args={[0.34, 0.42, 0.52]} />
        <meshPhysicalMaterial {...palette.edge} />
      </mesh>

      <Chassis>
        {
    /* Stabiliser legs and counterweight. */
  }
        {pair.map((side) => <group key={`stabiliser-${side}`}>
            <mesh
    position={[stabiliser.position[0], stabiliser.position[1], side * STABILISER_Z]}
    rotation={stabiliser.rotation}
  >
              <boxGeometry args={stabiliser.args} />
              <meshPhysicalMaterial {...palette.body} />
            </mesh>
            <mesh position={[-2.36, 0.06, side * STABILISER_Z]}>
              <boxGeometry args={[0.3, 0.08, 0.2]} />
              <meshPhysicalMaterial {...palette.edge} />
            </mesh>
          </group>)}
        <mesh position={[-2.14, 0.82, 0]}>
          <boxGeometry args={[0.24, 0.42, 0.95]} />
          <meshPhysicalMaterial {...palette.chassis} />
        </mesh>
      </Chassis>
    </group>;
}
export {
  JcbBody
};
